import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ExternalLink, ChevronRight, AlertTriangle, Link2, EyeOff } from "lucide-react";
import { B, fig } from "../clients/record/BillingCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/billing`;
const FOCUS = "outline-none focus-visible:ring-2 focus-visible:ring-[#1F6FEB] rounded";
const clientHref = (id) => (id ? `/clients/${encodeURIComponent(id)}` : "#");

const Head = ({ children }) => (
  <div className="text-[10px] font-bold uppercase tracking-[0.1em] mb-1.5" style={{ color: B.ink3 }}>
    {children}
  </div>
);

/* One MRR, two books. Stripe is what we collect; Contracted is the legacy
   Allocate/Operator book invoiced outside Stripe. A client is never in both and
   anyone inactive is in neither. */
export const MrrPanel = () => {
  const [d, setD] = useState(null);
  const [q, setQ] = useState(null);
  const [show, setShow] = useState(false);
  const [showQuotes, setShowQuotes] = useState(false);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/mrr`),
        axios.get(`${API}/mrr/unlinked-quotes`),
      ]);
      setD(a.data); setQ(b.data);
    } catch { /* the page's own error banner covers this */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (quote, clientId, exclude) => {
    setBusy(quote.quote_id);
    try {
      await axios.post(`${API}/mrr/link-quote`,
        { quote_id: quote.quote_id, client_id: clientId || null, exclude: !!exclude });
      toast.success(clientId ? "Quote attached — its revenue now counts against that client"
                             : "Quote marked as not revenue");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update that quote"); }
    finally { setBusy(""); }
  };

  if (!d) return null;
  const home = (d.currencies || [])[0];
  const na = d.needs_attention || {};
  const churn = (d.excluded_inactive || [])[0];

  return (
    <>
      <div className="rounded-[11px] p-3.5" style={{ background: B.card, border: `1px solid ${B.border}` }}
        data-testid="billing-true-mrr">
        <Head>True monthly recurring revenue</Head>
        {(d.currencies || []).length === 0 ? (
          <p className="text-[12.5px]" style={{ color: B.ink3 }}>
            Nothing recurring yet — no live Stripe subscriptions and no contracted figures set.
          </p>
        ) : (d.currencies || []).map((c) => (
          <div key={c.currency} className="mb-1.5">
            <div className="mono text-[26px] leading-none" style={{ color: B.ink }}
              data-testid={`billing-true-mrr-${c.currency}`}>
              {fig({ amount: c.total, currency: c.currency })}
            </div>
            <div className="text-[12.5px] mt-1" style={{ color: B.ink2 }}>
              Stripe <span className="mono">{fig({ amount: c.stripe, currency: c.currency })}</span>
              {" · "}Invoiced <span className="mono">{fig({ amount: c.contracted, currency: c.currency })}</span>
            </div>
          </div>
        ))}
        <p className="text-[11.5px] mt-1" style={{ color: B.ink3 }}>
          Stripe counts wherever it collects; the contracted legacy book counts only where it does
          not. Inactive clients count for nothing
          {churn ? <> — <span className="mono">{fig({ amount: churn.monthly, currency: churn.currency })}</span> of
            departed revenue sits outside this figure</> : null}.
        </p>

        <div className="flex flex-wrap gap-3 mt-2">
          {home && (
            <button type="button" onClick={() => setShow(!show)} data-testid="billing-mrr-toggle-clients"
              className={`inline-flex items-center gap-1 text-[12px] font-semibold ${FOCUS}`}
              style={{ color: B.accentInk }}>
              {show ? "Hide" : "Show"} every client counted ({(d.clients || []).length})
              <ChevronRight className="w-3 h-3" style={{ transform: show ? "rotate(90deg)" : "none" }} />
            </button>
          )}
          {q?.total_quotes > 0 && (
            <button type="button" onClick={() => setShowQuotes(!showQuotes)} data-testid="billing-mrr-toggle-quotes"
              className={`inline-flex items-center gap-1 text-[12px] font-semibold ${FOCUS}`}
              style={{ color: B.accentInk }}>
              {q.total_quotes} accepted quotes belong to nobody
              <ChevronRight className="w-3 h-3" style={{ transform: showQuotes ? "rotate(90deg)" : "none" }} />
            </button>
          )}
        </div>

        {(na.active_clients_with_no_figure || []).length > 0 && (
          <p className="text-[11.5px] mt-2" style={{ color: B.ink3 }} data-testid="billing-mrr-nofigure">
            {na.active_clients_with_no_figure.length} active clients have no Stripe subscription and no
            contracted figure, so they count as nothing. Set their Contracted MRR on the client record
            if they are paying.
          </p>
        )}
        {(na.paying_but_inactive || []).length > 0 && (
          <div className="mt-2 rounded-md p-2" style={{ background: "#FFF8D6", border: "1px solid #E8C86A" }}
            data-testid="billing-mrr-paying-inactive">
            <div className="flex items-center gap-1.5 text-[12px] font-semibold" style={{ color: "#6B5600" }}>
              <AlertTriangle className="w-3.5 h-3.5" /> Still paying, but marked inactive
            </div>
            {na.paying_but_inactive.map((r) => (
              <div key={r.client_id} className="text-[12px] mt-0.5" style={{ color: "#6B5600" }}>
                {r.company_name} — <span className="mono">{fig({ amount: r.monthly / 100, currency: r.currency })}</span>/mo
                is arriving and is not being counted.
              </div>
            ))}
          </div>
        )}
        {(na.unmatched_stripe || []).length > 0 && (
          <p className="text-[11.5px] mt-2" style={{ color: "#8A6D00" }} data-testid="billing-mrr-unmatched">
            {na.unmatched_stripe.length} Stripe subscription(s) are counted but not attached to a client
            — match them so the revenue has an owner.
          </p>
        )}
      </div>

      {show && (
        <div className="rounded-[11px] overflow-hidden" style={{ background: B.card, border: `1px solid ${B.border}` }}
          data-testid="billing-mrr-clients">
          {(d.clients || []).map((r) => (
            <div key={`${r.client_id}-${r.source}`} data-testid={`billing-mrr-client-${r.client_id}`}
              className="px-3.5 py-2 grid gap-3 items-center text-[12.5px]"
              style={{ gridTemplateColumns: "minmax(0,1fr) 96px 96px", borderBottom: `1px solid ${B.divider}` }}>
              <a href={clientHref(r.client_id)} className={`truncate ${FOCUS}`}
                style={{ color: B.ink, fontWeight: 600 }}>{r.company_name}</a>
              <span className="text-[11px] font-bold uppercase tracking-[0.06em]"
                style={{ color: r.source === "stripe" ? "#0E6B3F" : B.ink3 }}>
                {r.source === "stripe" ? "Stripe" : "Invoiced"}
              </span>
              <span className="mono text-right" style={{ color: B.ink }}>
                {fig({ amount: r.monthly / 100, currency: r.currency })}
              </span>
            </div>
          ))}
        </div>
      )}

      {showQuotes && q && (
        <div className="rounded-[11px] overflow-hidden" style={{ background: B.card, border: `1px solid ${B.border}` }}
          data-testid="billing-unlinked-quotes">
          <div className="px-3.5 py-2.5" style={{ background: B.panel, borderBottom: `1px solid ${B.border}` }}>
            <Head>Accepted quotes with no client record</Head>
            <p className="text-[11.5px]" style={{ color: B.ink3 }}>
              These carry recurring value but are attached to a prospect or to nothing, so their revenue
              counts for nobody. Attach each to its client, or mark it as not revenue to retire it.
            </p>
          </div>
          {(q.groups || []).map((g) => (
            <div key={`${g.name}-${g.currency}`} className="px-3.5 py-2"
              style={{ borderBottom: `1px solid ${B.divider}` }}
              data-testid={`billing-unlinked-group-${g.name.replace(/\s+/g, "-")}`}>
              <div className="flex items-baseline gap-2">
                <span className="text-[12.5px] font-semibold truncate" style={{ color: B.ink }}>{g.name}</span>
                <span className="mono text-[12px]" style={{ color: B.ink2 }}>{fig(g)}</span>
                <span className="text-[11px]" style={{ color: B.ink3 }}>
                  {g.quotes.length} quote{g.quotes.length === 1 ? "" : "s"}
                </span>
              </div>
              {g.quotes.map((row) => (
                <div key={row.quote_id} className="flex flex-wrap items-center gap-2 mt-1 pl-1">
                  <span className="text-[11.5px]" style={{ color: B.ink3 }}>{row.accepted_at}</span>
                  <span className="mono text-[11.5px]" style={{ color: row.excluded ? B.ink3 : B.ink2 }}>
                    {fig(row)}
                  </span>
                  {row.excluded && (
                    <span className="text-[10px] font-bold uppercase" style={{ color: B.ink3 }}>not revenue</span>
                  )}
                  <select defaultValue="" disabled={busy === row.quote_id}
                    data-testid={`billing-quote-link-${row.quote_id}`}
                    onChange={(e) => e.target.value && act(row, e.target.value, false)}
                    className="h-7 rounded-md border border-[#DAD5CA] bg-white px-1.5 text-[11.5px] max-w-[210px]">
                    <option value="">Attach to client…</option>
                    {(q.clients || []).map((c) => (
                      <option key={c.client_id} value={c.client_id}>{c.company_name}</option>
                    ))}
                  </select>
                  {!row.excluded && (
                    <button type="button" onClick={() => act(row, null, true)} disabled={busy === row.quote_id}
                      data-testid={`billing-quote-exclude-${row.quote_id}`}
                      className={`inline-flex items-center gap-1 text-[11.5px] ${FOCUS}`}
                      style={{ color: B.ink3 }}>
                      {busy === row.quote_id ? <Loader2 className="w-3 h-3 animate-spin" />
                                             : <EyeOff className="w-3 h-3" />}
                      Not revenue
                    </button>
                  )}
                  {row.excluded && (
                    <button type="button" onClick={() => act(row, null, false)} disabled={busy === row.quote_id}
                      data-testid={`billing-quote-restore-${row.quote_id}`}
                      className={`inline-flex items-center gap-1 text-[11.5px] ${FOCUS}`}
                      style={{ color: B.accentInk }}>
                      <Link2 className="w-3 h-3" /> Count it again
                    </button>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </>
  );
};
